/**
 * Interface icons, rasterised on demand.
 *
 * Emoji are ambiguous across platforms and unreadable at HUD size, so every
 * resource and stat gets a purpose-drawn 12x12 glyph that matches the pixel
 * look of the world. They are exported as data URLs for the DOM UI.
 *
 * **Units and buildings do not use these.** They show their own sprite — the
 * literal drawing that appears on the map — so a button can never promise a
 * picture the world does not deliver. See `iconUrl`.
 */

import { STRUCTURE_DEFS, UNIT_DEFS } from '../../shared/constants';
import { structIconUrl, unitIconUrl } from './sprites';

const PAL: Record<string, string> = {
  y: '#f0c95a', // wheat / gold
  Y: '#fff3b0', // highlight
  o: '#8a6a22', // dark gold edge
  g: '#5fa845', // stem green
  G: '#2f6d2c', // dark green
  w: '#8b5e34', // wood
  W: '#b8834f', // light wood
  n: '#8d8b86', // stone
  N: '#c6c3bc', // light stone
  d: '#4a4844', // stone shadow
  v: '#9a6ff0', // mana
  V: '#d9c2ff', // mana light
  m: '#d8dde8', // metal
  M: '#98a0b0', // dark metal
  s: '#e8b98a', // skin
  P: '#6a8fd0', // people / generic blue
  r: '#e0664a', // red
  b: '#3a2a1e', // dark brown
  k: '#20202a', // outline
};

const ICONS: Record<string, string[]> = {
  /* ---------------------------------------------------------- resources */
  food: [
    '    y  y    ',
    '   yyy yy   ',
    '   yyyyyy   ',
    '  yyyyyyyy  ',
    '   yyyyyy   ',
    '    yyyy    ',
    '     gg     ',
    '    ggg     ',
    '   g gg     ',
    '     gg     ',
    '     gg     ',
    '            ',
  ],
  wood: [
    '            ',
    '  wwwwwwww  ',
    ' wWWWWWWWWw ',
    ' wWwwwwwwWw ',
    ' wWwWWWWwWw ',
    ' wWwWooWwWw ',
    ' wWwWWWWwWw ',
    ' wWwwwwwwWw ',
    ' wWWWWWWWWw ',
    '  wwwwwwww  ',
    '            ',
    '            ',
  ],
  stone: [
    '            ',
    '    nnnn    ',
    '   nNNNnn   ',
    '  nNNNNnnn  ',
    ' nNNNnnnnnn ',
    ' nNNnnnnnnn ',
    ' nnnnnnnnnn ',
    '  nnnnnnnn  ',
    '   dddddd   ',
    '            ',
    '            ',
    '            ',
  ],
  mana: [
    '     V      ',
    '    VVV     ',
    '   VvvvV    ',
    '   VvvvV    ',
    '  VvvVvvV   ',
    '  VvvVvvV   ',
    '   VvvvV    ',
    '    VvV     ',
    '     V      ',
    '            ',
    '            ',
    '            ',
  ],
  shrine: [
    '     v      ',
    '    vvv     ',
    '   vVvVv    ',
    '   vVvVv    ',
    '  vvVVVvv   ',
    '   vvvvv    ',
    '    vvv     ',
    '   nnnnn    ',
    '   nnnnn    ',
    '            ',
    '            ',
    '            ',
  ],
  gold: [
    '            ',
    '    oooo    ',
    '   oyyyyo   ',
    '  oyYyyYyo  ',
    '  oyyYYyyo  ',
    '  oyYyyYyo  ',
    '  oyyYYyyo  ',
    '   oyyyyo   ',
    '    oooo    ',
    '            ',
    '            ',
    '            ',
  ],
  /* --------------------------------------------------------------- stats */
  troops: [
    '         m  ',
    '        mm  ',
    '       mmm  ',
    '      mmm   ',
    '     mmm    ',
    '    mmm     ',
    '   mmm      ',
    '  wmm       ',
    ' www        ',
    '  w w       ',
    '            ',
    '            ',
  ],
  /*
   * **Two people, standing apart.**
   *
   * The old drawing had two heads over one merged slab of body, which at twelve
   * pixels read as a single wide person rather than as a population. Splitting
   * them with a clear column of nothing down the middle is the whole trick:
   * "more than one" is carried by the gap, not by the detail.
   */
  pop: [
    '            ',
    '   ss  ss   ',
    '   ss  ss   ',
    '  pPPp pPPp ',
    '  pPPp pPPp ',
    '   PP   PP  ',
    '   PP   PP  ',
    '  P  P P  P ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  land: [
    '            ',
    '  GGGGGGGG  ',
    ' GggggggggG ',
    ' Ggg ggg gG ',
    ' GggggggggG ',
    ' Gg gggg gG ',
    ' GggggggggG ',
    '  GGGGGGGG  ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  /* ---------------------------------------------------------------- units */
  knight: [
    '            ',
    '    mmmm    ',
    '   mmmmmm   ',
    '   mkkkkm   ',
    '   mmmmmm   ',
    '  PPPPPPPP  ',
    ' PPPPPPPPPP ',
    ' PP PPPP PP ',
    '    PPPP    ',
    '    P  P    ',
    '            ',
    '            ',
  ],
  archer: [
    '            ',
    '     w    y ',
    '    w    y  ',
    '   w    y   ',
    '   w   y    ',
    '   w  yyyyy ',
    '   w   y    ',
    '   w    y   ',
    '    w    y  ',
    '     w    y ',
    '            ',
    '            ',
  ],
  wizard: [
    '     v      ',
    '    vvv     ',
    '   vvvvv    ',
    '  vvvvvvv   ',
    '   sssss  y ',
    '   sPPPs  w ',
    '   PPPPP  w ',
    '   PPPPP  w ',
    '    PPP   w ',
    '    P P     ',
    '            ',
    '            ',
  ],
  thief: [
    '            ',
    '    kkkk    ',
    '   kkkkkk   ',
    '   kyykyk   ',
    '   kkkkkk   ',
    '  PPPPPPPP  ',
    ' PPPPPPPP m ',
    ' PP PPPP mm ',
    '    PPPP m  ',
    '    P  P    ',
    '            ',
    '            ',
  ],
  dragon: [
    '            ',
    '  b     bb  ',
    ' brb   brrb ',
    ' brrb rrrrk ',
    '  brrrrrry  ',
    '  brrrrrr   ',
    '   brrrrb   ',
    '    r  r    ',
    '    b  b    ',
    '            ',
    '            ',
    '            ',
  ],
  /* ------------------------------------------------------------ buildings */
  keep: [
    '            ',
    ' n n  n  n  ',
    ' nnnnnnnnn  ',
    ' nnnnnnnnn  ',
    ' nnnPPnnnn  ',
    ' nNnPPnnNn  ',
    ' nnnnnnnnn  ',
    ' nnnkkknnn  ',
    ' nnnkkknnn  ',
    ' nnnnnnnnn  ',
    '            ',
    '            ',
  ],
  farm: [
    '            ',
    '   bbbbbb   ',
    '  byyyyyyb  ',
    '  bbbbbbbb  ',
    '  yy yy yy  ',
    '  yy yy yy  ',
    '  gg gg gg  ',
    '  bbbbbbbb  ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  lumber: [
    '     GG     ',
    '    GGGG    ',
    '   GGGGGG   ',
    '  GGGGGGGG  ',
    '   GGGGGG   ',
    '     ww     ',
    '  m  ww     ',
    '  mwwwwb    ',
    '   bbbb     ',
    '            ',
    '            ',
    '            ',
  ],
  quarry: [
    '            ',
    '     nn     ',
    '    nNNn    ',
    '   nNNNNn   ',
    '  nNNNNNNn  ',
    ' nnddddddnn ',
    ' nnd PP dnn ',
    ' nnd PP dnn ',
    ' nnnnnnnnnn ',
    '            ',
    '            ',
    '            ',
  ],
  village: [
    '            ',
    '   r    r   ',
    '  rrr  rrr  ',
    ' rrrrrrrrrr ',
    ' wwww wwww  ',
    ' wPww wwPw  ',
    ' wwww wwww  ',
    ' wwww wwww  ',
    '  bbb  bbb  ',
    '            ',
    '            ',
    '            ',
  ],
  barracks: [
    '     m      ',
    '    mmm     ',
    '   PPPPP    ',
    '  PPPPPPP   ',
    ' PPPPPPPPP  ',
    ' PPkkkkkPP  ',
    ' PPkkkkkPP  ',
    ' bbbbbbbbb  ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  den: [
    '            ',
    '   kkkkkk   ',
    '  kkkkkkkk  ',
    '  kbbbbbbk  ',
    '  kbPPPPbk  ',
    '  kbkkkkbk  ',
    '  kbbbbbbk  ',
    '  kkkkkkkk  ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  tower: [
    '     V      ',
    '    VvV     ',
    '   nnvnn    ',
    '   nPPPn    ',
    '   nnnnn    ',
    '   nPPPn    ',
    '   nnnnn    ',
    '  nnnnnnn   ',
    '  nnnnnnn   ',
    '            ',
    '            ',
    '            ',
  ],
  roost: [
    '            ',
    '   n    n   ',
    '  nnPPPPnn  ',
    ' nPPPPPPPPn ',
    ' nPPrrrrPPn ',
    ' bbbbbbbbbb ',
    '  bwwwwwwb  ',
    '   bwwwwb   ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  watchtower: [
    '            ',
    '   kkkkkk   ',
    '   nPPPPn   ',
    '   nnnnnn   ',
    '    nnnn    ',
    '    nnnn    ',
    '    nnnn    ',
    '   nnnnnn   ',
    '  nnnnnnnn  ',
    '            ',
    '            ',
    '            ',
  ],
  market: [
    '            ',
    '  y      y  ',
    '  yyyyyyyy  ',
    '  y  mm  y  ',
    '  y mmmm y  ',
    '  ymmmmmmy  ',
    '  bPPPPPPb  ',
    '  bbbbbbbb  ',
    '  b      b  ',
    '            ',
    '            ',
    '            ',
  ],
  harbour: [
    '            ',
    '     m      ',
    '     mm     ',
    '     mmm    ',
    '     m      ',
    '  wwwwwwww  ',
    '   wwwwww   ',
    '  PPPPPPPP  ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  warehouse: [
    '            ',
    '  wwwwwwww  ',
    ' wWWWWWWWWw ',
    ' wwwwwwwwww ',
    ' nnnnnnnnnn ',
    ' nyyn  nyyn ',
    ' nyyn  nyyn ',
    ' nnnn  nnnn ',
    ' nnnnnnnnnn ',
    '            ',
    '            ',
    '            ',
  ],
  /* ------------------------------------------------- dwarven buildings */
  forgehall: [
    '   m        ',
    '   m        ',
    '  nnnnnnn   ',
    ' nnnnnnnnn  ',
    ' nnrrrrrnn  ',
    ' nnrrrrrnn  ',
    ' nnnnnnnnn  ',
    ' nnkkkkknn  ',
    ' nnnnnnnnn  ',
    '            ',
    '            ',
    '            ',
  ],
  deeptunnel: [
    '            ',
    '  nnnnnnnn  ',
    ' nnnnnnnnnn ',
    ' nnkkkkkknn ',
    ' nkkddddknn ',
    ' nkddddddkn ',
    ' nnkkkkkknn ',
    ' wwwwwwwwww ',
    '  w  w  w   ',
    '            ',
    '            ',
    '            ',
  ],
  runehall: [
    '     v      ',
    '    vvv     ',
    '  nnnnnnn   ',
    ' nvNNNNNvn  ',
    ' nNvNNNvNn  ',
    ' nNNvNvNNn  ',
    ' nvNNNNNvn  ',
    ' nnnnnnnnn  ',
    '  nnnnnnn   ',
    '            ',
    '            ',
    '            ',
  ],
  foundry: [
    '   mm       ',
    '   mm       ',
    '  nnnnnnn   ',
    ' nnrrrrrnn  ',
    ' nrrrrrrrn  ',
    ' nrrrrrrrn  ',
    ' nnrrrrrnn  ',
    ' nnnnnnnnn  ',
    ' dd     dd  ',
    '            ',
    '            ',
    '            ',
  ],
  /* --------------------------------------------------- elven buildings */
  bowyer: [
    '     gg     ',
    '   gggggg   ',
    '  gggggggg  ',
    ' gggggggggg ',
    '  gggggggg  ',
    '   gggggg   ',
    '     ww     ',
    '    wwww    ',
    '    wwww    ',
    '            ',
    '            ',
    '            ',
  ],
  shadowglade: [
    '     GG     ',
    '   GGGGGG   ',
    '  GGGkkGGG  ',
    ' GGkkkkkkGG ',
    '  GkkkkkkG  ',
    '   Gkkkkg   ',
    '     ww     ',
    '    wwww    ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  moongrove: [
    '   V   V    ',
    '  V     V   ',
    '  V     V   ',
    '   VV VV    ',
    '    gggg    ',
    '   gggggg   ',
    '  gggggggg  ',
    '     ww     ',
    '    wwww    ',
    '            ',
    '            ',
    '            ',
  ],
  aerie: [
    '     r      ',
    '    rrr     ',
    '   ryyyr    ',
    '  rryyyrr   ',
    '   ryyyr    ',
    '  wwwwwww   ',
    ' wwwwwwwww  ',
    '  ww   ww   ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  /* ------------------------------------------------------ elite units */
  golem: [
    '            ',
    '   nnnnnn   ',
    '  nNvNNvNn  ',
    '  nNNNNNNn  ',
    ' nnNNNNNNnn ',
    ' nnNNNNNNnn ',
    ' nnnnnnnnnn ',
    '  nn    nn  ',
    '  nn    nn  ',
    '  dd    dd  ',
    '            ',
    '            ',
  ],
  phoenix: [
    '            ',
    '  r      rr ',
    ' ryr    ryyr',
    ' ryyr  ryyr ',
    '  ryyyyyyk  ',
    '   yyyyyyy  ',
    '  ryyyyyyr  ',
    '   rr  rr   ',
    '            ',
    '            ',
    '            ',
    '            ',
  ],
  hammer: [
    '            ',
    '   mmmmmm   ',
    '  mMMMMMMm  ',
    '  mMMMMMMm  ',
    '   mmwwmm   ',
    '     ww     ',
    '     ww     ',
    '     ww     ',
    '     ww     ',
    '    wwww    ',
    '            ',
    '            ',
  ],
  wall: [
    '            ',
    '            ',
    ' nn nn nn n ',
    ' nnnnnnnnnn ',
    ' nNNnNNnNNn ',
    ' nnnnnnnnnn ',
    ' nNNnNNnNNn ',
    ' nnnnnnnnnn ',
    ' dddddddddd ',
    '            ',
    '            ',
    '            ',
  ],
  gate: [
    '            ',
    '            ',
    ' nn nn nn n ',
    ' nnnnnnnnnn ',
    ' nnnyyyynnn ',
    ' nn kkkk nn ',
    ' nn kkkk nn ',
    ' nnnkkkknnn ',
    ' dddddddddd ',
    '            ',
    '            ',
    '            ',
  ],
  caravan: [
    '            ',
    '   WWWWWW   ',
    '  WyyyyyyW  ',
    '  WyyyyyyW  ',
    '  wwwwwwww  ',
    '  w      w  ',
    '  kk    kk  ',
    ' kkkk  kkkk ',
    '  kk    kk  ',
    '            ',
    '            ',
    '            ',
  ],

  /* ------------------------------------------- what a building is *for*
   * Three shapes, no colour-matching required, readable at twelve pixels and
   * distinguishable from each other by silhouette alone — which is the whole
   * job. See `buildingKind`.
   */
  /** economy: a coin's ring */
  'kind-econ': [
    '            ',
    '    yyyy    ',
    '   yy  yy   ',
    '  yy    yy  ',
    '  y      y  ',
    '  y      y  ',
    '  y      y  ',
    '  yy    yy  ',
    '   yy  yy   ',
    '    yyyy    ',
    '            ',
    '            ',
  ],
  /** military: a spearhead */
  'kind-mil': [
    '            ',
    '     mm     ',
    '     mm     ',
    '    mmmm    ',
    '    mmmm    ',
    '   mmmmmm   ',
    '   mmmmmm   ',
    '  mmmmmmmm  ',
    '  mmmmmmmm  ',
    ' mmmmmmmmmm ',
    '            ',
    '            ',
  ],
  /** housing: a roof over a room */
  'kind-home': [
    '            ',
    '     WW     ',
    '    WWWW    ',
    '   WWWWWW   ',
    '  WWWWWWWW  ',
    ' WWWWWWWWWW ',
    '   wwwwww   ',
    '   wwwwww   ',
    '   wwwwww   ',
    '   wwwwww   ',
    '            ',
    '            ',
  ],
};

/**
 * Where a 12-pixel icon may honestly stand in for another.
 *
 * **Soldiers, yes; buildings, no.** At this size a dwarf axe and an elf blade
 * really are the same four pixels, and a consistent role glyph reads better
 * than a muddy one — a Shieldbearer and a Knight are both "armoured man with a
 * weapon". A building is not like that: a Bowyer Glade is a green tree and a
 * Forge Hall is a stone chimney, and showing a human Barracks for either is
 * simply the wrong picture. Every racial hall now has its own glyph, and so do
 * the two elites that look nothing like a dragon.
 */
const ROLE_ALIAS: Record<string, string> = {
  shieldbearer: 'knight', blademaster: 'knight',
  crossbow: 'archer', longbow: 'archer',
  runesmith: 'wizard', druid: 'wizard',
  sapper: 'thief', shadowdancer: 'thief',
  boat: 'harbour',
};

const cache: Record<string, string> = {};

function rasterize(art: string[], scale: number): string {
  const h = art.length;
  const w = art[0].length;
  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = art[y][x] || ' ';
      const color = PAL[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }
  return canvas.toDataURL();
}

/**
 * Which unit and building keys have a real sprite to show instead of a glyph.
 *
 * Built once from the definition tables, so a key can never point at the wrong
 * picture: `def.key` is the same string the map uses to draw the thing itself.
 */
const SPRITE_FOR_KEY: Record<string, () => string> = (() => {
  const out: Record<string, () => string> = {};
  UNIT_DEFS.forEach((def, type) => { out[def.key] = () => unitIconUrl(type); });
  STRUCTURE_DEFS.forEach((def, type) => { out[def.key] = () => structIconUrl(type); });
  return out;
})();

/**
 * data: URL for an icon, cached.
 *
 * **A unit or a building shows its own sprite.** The palette button, the wheel
 * slice and the tooltip therefore show exactly what will appear on the map —
 * which the separate glyph set could not promise, and repeatedly failed at: the
 * elf Bowyer Glade's glyph and its building were two different drawings.
 * Everything else — resources, the hammer, the wall, the interface furniture —
 * still comes from the glyph set here, because those have nothing on the map to
 * be a picture of.
 */
export function iconUrl(name: string, scale = 2): string {
  const sprite = SPRITE_FOR_KEY[name];
  if (sprite) {
    const key = `sprite:${name}`;
    if (cache[key] === undefined) cache[key] = sprite();
    if (cache[key]) return cache[key];
  }
  const resolved = ICONS[name] ? name : (ROLE_ALIAS[name] || name);
  const key = `${resolved}@${scale}`;
  if (cache[key]) return cache[key];
  const art = ICONS[resolved];
  if (!art) return '';
  cache[key] = rasterize(art, scale);
  return cache[key];
}

/** Inline <i> element markup for use inside innerHTML strings. */
export function ico(name: string, size = 14): string {
  const url = iconUrl(name);
  if (!url) return '';
  return `<i class="ico" style="background-image:url(${url});width:${size}px;height:${size}px"></i>`;
}

export const ICON_NAMES = Object.keys(ICONS);
